"use client";

import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Clipboard,
  Field,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import type { CreateFeedbackCollectionResult } from "@/generated/fastapi";
import { showErrorToast, showSuccessToast } from "@/shared/feedback";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import {
  type FeedbackCollectionLinkItem,
} from "../api/listFeedbackLinks";
import useCreateFeedbackLinkMutation from "../mutations/useCreateFeedbackLinkMutation";
import useRevokeFeedbackLinkMutation from "../mutations/useRevokeFeedbackLinkMutation";
import useFeedbackLinksQuery from "../queries/useFeedbackLinksQuery";
import FeedbackRequestCard from "./FeedbackRequestCard";

const DEFAULT_REQUEST_TITLE = "Professional feedback";

function buildPublicFeedbackUrl(publicUrlPath?: string | null) {
  if (!publicUrlPath) {
    return "";
  }

  if (typeof window === "undefined") {
    return publicUrlPath;
  }

  return new URL(publicUrlPath, window.location.origin).toString();
}

export default function FeedbackShareDrawer() {
  const [requestTitle, setRequestTitle] = useState(DEFAULT_REQUEST_TITLE);
  const [requestMessage, setRequestMessage] = useState("");
  const [recipientHint, setRecipientHint] = useState("");
  const [createdLink, setCreatedLink] =
    useState<CreateFeedbackCollectionResult | null>(null);
  const [createdUrlsByTokenId, setCreatedUrlsByTokenId] = useState<
    Record<string, string>
  >({});
  const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null);
  const feedbackLinksQuery = useFeedbackLinksQuery();
  const createFeedbackLinkMutation = useCreateFeedbackLinkMutation();
  const revokeFeedbackLinkMutation = useRevokeFeedbackLinkMutation();
  const links: FeedbackCollectionLinkItem[] = feedbackLinksQuery.data?.links ?? [];
  const canRevoke = Boolean(createdLink?.token_id);
  const createdUrl = useMemo(
    () => buildPublicFeedbackUrl(createdLink?.public_url_path),
    [createdLink?.public_url_path],
  );

  const handleCreate = async () => {
    try {
      const result = await createFeedbackLinkMutation.mutateAsync({
        request_title: requestTitle.trim() || null,
        request_message: requestMessage.trim() || null,
        recipient_hint: recipientHint.trim() || null,
        allow_text: true,
        allow_voice: true,
      });
      const createdUrl = buildPublicFeedbackUrl(result.public_url_path);
      setCreatedLink(result);
      if (result.token_id && createdUrl) {
        setCreatedUrlsByTokenId((current) => ({
          ...current,
          [result.token_id as string]: createdUrl,
        }));
      }
      showSuccessToast("Feedback link created.");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create feedback link.";
      showErrorToast(message);
    }
  };

  const handleRevoke = async (tokenId: string | null | undefined) => {
    if (!tokenId) {
      return;
    }

    setRevokingTokenId(tokenId);
    try {
      await revokeFeedbackLinkMutation.mutateAsync({ token_id: tokenId });
      if (createdLink?.token_id === tokenId) {
        setCreatedLink(null);
      }
      setCreatedUrlsByTokenId((current) => {
        const next = { ...current };
        delete next[tokenId];
        return next;
      });
      showSuccessToast("Feedback link revoked.");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to revoke feedback link.";
      showErrorToast(message);
    } finally {
      setRevokingTokenId(null);
    }
  };

  return (
    <Stack gap="5" align="stretch">
      <GenericDrawerInfo
        description="Create a private link for someone to share professional feedback. Their response becomes unprocessed source material in Backlog."
        items={[
          { label: "Shown once", colorPalette: "orange" },
          { label: "30 days", colorPalette: "blue" },
        ]}
      />

      <Stack gap="4" align="stretch">
        <Field.Root>
          <Field.Label>Request title</Field.Label>
          <Input
            value={requestTitle}
            onChange={(event) => setRequestTitle(event.target.value)}
            placeholder={DEFAULT_REQUEST_TITLE}
          />
        </Field.Root>

        <Field.Root>
          <Field.Label>Message for recipient</Field.Label>
          <Textarea
            value={requestMessage}
            onChange={(event) => setRequestMessage(event.target.value)}
            placeholder="What would you like them to reflect on?"
            minH="28"
          />
          <Field.HelperText>
            This message is shown on the public feedback page.
          </Field.HelperText>
        </Field.Root>

        <Field.Root>
          <Field.Label>Private recipient hint</Field.Label>
          <Input
            value={recipientHint}
            onChange={(event) => setRecipientHint(event.target.value)}
            placeholder="For your memory only"
          />
          <Field.HelperText>
            Keep track of who this link was meant for. This is not shown to the
            recipient.
          </Field.HelperText>
        </Field.Root>
      </Stack>

      {createdUrl ? (
        <Box
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="md"
          bg="moveon.surface.elevatedBg"
          p="3"
        >
          <Stack gap="3" align="stretch">
            <Text fontSize="sm" fontWeight="semibold">
              Feedback link
            </Text>
            <HStack gap="2" align="center">
              <Input value={createdUrl} readOnly />
              <Clipboard.Root value={createdUrl}>
                <Clipboard.Trigger asChild>
                  <Button variant="outline">
                    <Clipboard.Indicator />
                    Copy
                  </Button>
                </Clipboard.Trigger>
              </Clipboard.Root>
              {canRevoke ? (
                <Button
                  variant="ghost"
                  colorPalette="red"
                  onClick={() => handleRevoke(createdLink?.token_id)}
                  disabled={revokingTokenId === createdLink?.token_id}
                  loading={revokingTokenId === createdLink?.token_id}
                >
                  Revoke
                </Button>
              ) : null}
            </HStack>
            <Text fontSize="xs" color="moveon.text.muted">
              The raw link is shown once. Create a new link if you need another
              copy later.
            </Text>
          </Stack>
        </Box>
      ) : null}

      <HStack justify="flex-end">
        <Button
          variant="outline"
          onClick={() => {
            void feedbackLinksQuery.refetch();
          }}
          disabled={feedbackLinksQuery.isFetching}
          loading={feedbackLinksQuery.isFetching}
        >
          <GenericIcon icon="reload" size={iconSizePresets.inline} />
          Refresh
        </Button>
        <Button
          onClick={handleCreate}
          disabled={createFeedbackLinkMutation.isPending}
          loading={createFeedbackLinkMutation.isPending}
          bg="moveon.primary"
        >
          <GenericIcon icon="collect_feedback" size={iconSizePresets.inline} />
          Create link
        </Button>
      </HStack>

      <Stack gap="3" align="stretch">
        <Text fontSize="sm" fontWeight="semibold">
          Feedback requests
        </Text>
        {feedbackLinksQuery.isError ? (
          <Box
            borderWidth="1"
            borderColor="moveon.danger"
            borderRadius="md"
            bg="moveon.surface.elevatedBg"
            p="3"
          >
            <Text fontSize="sm" color="moveon.danger">
              {feedbackLinksQuery.error instanceof Error
                ? feedbackLinksQuery.error.message
                : "Failed to load feedback links."}
            </Text>
          </Box>
        ) : links.length === 0 ? (
          <Box
            borderWidth="1"
            borderColor="moveon.surface.border"
            borderRadius="md"
            bg="moveon.surface.elevatedBg"
            p="3"
          >
            <Text fontSize="sm" color="moveon.text.muted">
              No feedback links yet.
            </Text>
          </Box>
        ) : (
          links.map((link) => (
            <FeedbackRequestCard
              key={link.token_id}
              copyableUrl={
                buildPublicFeedbackUrl(link.public_url_path) ||
                createdUrlsByTokenId[link.token_id]
              }
              isRevoking={revokingTokenId === link.token_id}
              link={link}
              onRevoke={handleRevoke}
            />
          ))
        )}
      </Stack>
    </Stack>
  );
}
